import { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { Toaster as RadixToaster } from './components/ui/toaster';
import useGlobalKeyboardShortcuts from './hooks/useGlobalKeyboardShortcuts';
import { initGlobalAutoCapitalize } from './utils/autoCapitalize';

// Initialize global auto-capitalize for real-time uppercase typing & saving
initGlobalAutoCapitalize();

// Staff Restricted Route Component - blocks staff from accessing certain pages
function StaffRestrictedRoute({ user, children }) {
  if (user?.role === 'staff') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">Stock Audit and Stock Adjustment features are not available for staff users.</p>
          <p className="text-sm text-gray-500 mb-6">Please contact your manager if you need to perform stock adjustments.</p>
          <a href="/dashboard" className="inline-block bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            Go to Dashboard
          </a>
        </div>
      </div>
    );
  }
  return children;
}

// Auto Purchase Settings and Spotify Music Banner are lazy loaded to optimize initial bundle size

// Lazy load heavy components for better performance
const Login = lazy(() => import('./pages/Login'));
const AutoPurchaseSettings = lazy(() => import('./pages/AutoPurchaseSettings'));
const SpotifyMusicBanner = lazy(() => import('./components/SpotifyMusicBanner'));
const DavaaiApp = lazy(() => import('./davaai/DavaaiApp'));
const DavaaiAdmin = lazy(() => import('./davaai/DavaaiAdmin'));
const DavaaiRiderApp = lazy(() => import('./davaai/RiderApp'));
const DavaaiPortalManager = lazy(() => import('./pages/DavaaiPortalManager'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ItemMasterNew = lazy(() => import('./pages/ItemMasterNew'));
const CategoryMaster = lazy(() => import('./pages/CategoryMaster'));
const ImportItems = lazy(() => import('./pages/ImportItems'));
const BatchMaster = lazy(() => import('./pages/BatchMaster'));
const SupplierMaster = lazy(() => import('./pages/SupplierMaster'));
const PurchaseEntry = lazy(() => import('./pages/PurchaseEntry'));
const PurchaseHistory = lazy(() => import('./pages/PurchaseHistory'));
const PurchaseHistoryB2B = lazy(() => import('./pages/PurchaseHistoryB2B'));
const SalesInvoiceB2C = lazy(() => import('./pages/SalesInvoiceB2C'));
const SalesHistoryB2C = lazy(() => import('./pages/SalesHistoryB2C'));
const SalesInvoiceB2B = lazy(() => import('./pages/SalesInvoiceB2B'));
const SalesHistoryB2B = lazy(() => import('./pages/SalesHistoryB2B'));
const Inventory = lazy(() => import('./pages/Inventory'));
const StoreTrack = lazy(() => import('./pages/StoreTrackEnhanced'));
const LoyaltyDashboard = lazy(() => import('./pages/LoyaltyDashboard'));
const ShiftManagement = lazy(() => import('./pages/ShiftManagement'));
const ShiftCollection = lazy(() => import('./pages/ShiftCollection'));
const CustomerReceipt = lazy(() => import('./pages/CustomerReceiptNew'));
const LuckyDraw = lazy(() => import('./pages/LuckyDraw'));
const OffersPage = lazy(() => import('./pages/OffersPage'));
const LuckyDrawScreen1 = lazy(() => import('./pages/LuckyDrawScreen1'));
const LuckyDrawScreen2 = lazy(() => import('./pages/LuckyDrawScreen2'));
const LuckyDrawLive = lazy(() => import('./pages/LuckyDrawLive'));
const LuckyDrawView = lazy(() => import('./pages/LuckyDrawView'));

// Returns Management Pages
const PurchaseReturnRequest = lazy(() => import('./pages/PurchaseReturnRequest'));
const MyReturnRequests = lazy(() => import('./pages/MyReturnRequests'));
const ReturnApprovalDashboard = lazy(() => import('./pages/ReturnApprovalDashboard'));
const TrayManagementDashboard = lazy(() => import('./pages/TrayManagementDashboard'));
const PurchaseReturnView = lazy(() => import('./pages/PurchaseReturnView'));
const ReturnPolicySettings = lazy(() => import('./pages/ReturnPolicySettings'));

// NEW Return Module Pages (2025-11-25) - Using lazy loading for React 19 Suspense compatibility
const StoreReturnToWarehouse = lazy(() => import('./pages/StoreReturnToWarehouse'));
const StoreReturnHistory = lazy(() => import('./pages/StoreReturnHistory'));
const ConsumerReturn = lazy(() => import('./pages/ConsumerReturn'));
const WarehouseTrayManagement = lazy(() => import('./pages/WarehouseTrayManagement'));
const SupplierReturnGeneration = lazy(() => import('./pages/SupplierReturnGeneration'));
const ManualSupplierReturn = lazy(() => import('./pages/ManualSupplierReturn'));
const WarehouseSalesReturn = lazy(() => import('./pages/WarehouseSalesReturn'));
const CreditNote = lazy(() => import('./pages/CreditNote'));
const PrintTemplateBuilder = lazy(() => import('./pages/PrintTemplateBuilder'));
const PaytmPaymentDashboard = lazy(() => import('./pages/PaytmPaymentDashboard'));
const KeyboardShortcutsHelp = lazy(() => import('./pages/KeyboardShortcutsHelp'));
const DataMigration = lazy(() => import('./pages/DataMigration'));
const StockMigrationDryRun = lazy(() => import('./pages/StockMigrationDryRun'));
const PaytmPOSManager = lazy(() => import('./pages/PaytmPOSManager'));
const EInvoiceManagement = lazy(() => import('./pages/EInvoiceManagement'));
const GSTR1Dashboard = lazy(() => import('./pages/GSTR1Dashboard'));
const GSTR3BDashboard = lazy(() => import('./pages/GSTR3BDashboard'));
const SupplierPaymentDebug = lazy(() => import('./pages/SupplierPaymentDebug'));
const FranchiseDiscountImport = lazy(() => import('./pages/FranchiseDiscountImport'));
const BatchImportOldCode = lazy(() => import('./pages/BatchImportOldCode'));
const DatabaseBackup = lazy(() => import('./pages/IncrementalBackup'));
const PrintPreviewPage = lazy(() => import('./pages/PrintPreviewPage'));

// GBP Command Center
const GBPCommandCenter = lazy(() => import('./pages/GBPCommandCenter'));

// Dispatch Register Pages
const DispatchRegister = lazy(() => import('./pages/DispatchRegister'));
const PackerInterface = lazy(() => import('./pages/PackerInterface'));

// Report Pages
const OverallPerformance = lazy(() => import('./pages/OverallPerformance'));
const SalesRegister = lazy(() => import('./pages/SalesRegister'));
const MarginPerformanceReport = lazy(() => import('./pages/MarginPerformanceReport'));
const ConsumerMergePerformance = lazy(() => import('./pages/ConsumerMergePerformance'));
const PurchaseRegister = lazy(() => import('./pages/PurchaseRegister'));
const TargetPerformance = lazy(() => import('./pages/TargetPerformance'));

// Staff Profile Pages
const StaffProfile = lazy(() => import('./pages/StaffProfile'));
const StaffIDCard = lazy(() => import('./pages/StaffIDCard'));
const StaffList = lazy(() => import('./pages/StaffList'));

// Sales Order Pages (Customer)
const SalesOrderEntry = lazy(() => import('./pages/SalesOrderEntry'));
const SalesOrderHistory = lazy(() => import('./pages/SalesOrderHistory'));
const OnlineOrders = lazy(() => import('./pages/OnlineOrders'));

const OrderFulfillmentDashboard = lazy(() => import('./pages/OrderFulfillmentDashboard'));
const TrackOrders = lazy(() => import('./pages/TrackOrders'));
const PharmarackAutoOrder = lazy(() => import('./pages/PharmarackAutoOrder'));
const PerfConsole = lazy(() => import('./pages/PerfConsole'));
import { setRequestIdentity } from './utils/requestIdentity';
const MobileDashboard = lazy(() => import('./pages/mobile/MobileDashboard'));
const MobileSalesB2C = lazy(() => import('./pages/mobile/MobileSalesB2C'));
const MobileSalesB2B = lazy(() => import('./pages/mobile/MobileSalesB2B'));
const MobilePurchase = lazy(() => import('./pages/mobile/MobilePurchase'));
const MobileStockAudit = lazy(() => import('./pages/mobile/MobileStockAudit'));
const MobileWMS = lazy(() => import('./pages/mobile/MobileWMSV2'));

// HR Module Pages
const HRDashboard = lazy(() => import('./pages/HRDashboard'));
const HRAttendance = lazy(() => import('./pages/HRAttendance'));
const HRTasks = lazy(() => import('./pages/HRTasks'));
const HRLeaves = lazy(() => import('./pages/HRLeaves'));
const HRApprovals = lazy(() => import('./pages/HRApprovals'));
const HRReports = lazy(() => import('./pages/HRReports'));
const HRPayouts = lazy(() => import('./pages/HRPayouts'));
const HRSettings = lazy(() => import('./pages/HRSettings'));

// Expense Module - Cash Expense Monitor with Shift Integration
const ExpenseModule = lazy(() => import('./pages/ExpenseModule'));
const PendingInvoicesReport = lazy(() => import('./pages/PendingInvoicesReport'));

// Year End Closing
const YearEndClosing = lazy(() => import('./pages/YearEndClosing'));

// Notification Center
const NotificationCenter = lazy(() => import('./pages/NotificationCenter'));

// Vendor Portal - Supplier PWA
const SupplierLogin = lazy(() => import('./pages/VendorPortal/SupplierLogin'));
const SupplierDashboard = lazy(() => import('./pages/VendorPortal/SupplierDashboard'));
const PurchaseRequirements = lazy(() => import('./pages/VendorPortal/PurchaseRequirements'));
const VendorConfirmedOrders = lazy(() => import('./pages/VendorPortal/ConfirmedOrders'));
const SupplierProfile = lazy(() => import('./pages/VendorPortal/SupplierProfile'));
const BusinessPortfolio = lazy(() => import('./pages/VendorPortal/BusinessPortfolio'));
const OutstandingAccount = lazy(() => import('./pages/VendorPortal/OutstandingAccount'));
const VendorNotifications = lazy(() => import('./pages/VendorPortal/Notifications'));
const VendorOwnPlatform = lazy(() => import('./pages/VendorPortal/OwnPlatformEnhanced'));

// Vendor Admin - ERP side for managing requirements
const VendorRequirements = lazy(() => import('./pages/VendorAdmin/VendorRequirements'));
const CreateRequirement = lazy(() => import('./pages/VendorAdmin/CreateRequirement'));
const RequirementDetails = lazy(() => import('./pages/VendorAdmin/RequirementDetails'));
const VendorPerformance = lazy(() => import('./pages/VendorAdmin/VendorPerformance'));
const AdminOwnPlatform = lazy(() => import('./pages/VendorAdmin/AdminOwnPlatform'));

// Loading component
const LoadingScreen = () => (
  <div className="flex items-center justify-center h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
    <div className="text-center">
      <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-600 font-medium">Loading QuickRx...</p>
    </div>
  </div>
);

// Non-lazy imports (keep these as they're lightweight or needed immediately)
const GmailImport = lazy(() => import('./pages/GmailImport'));
const SupplierCSVMapper = lazy(() => import('./pages/SupplierCSVMapper'));
const ItemCSVMapper = lazy(() => import('./pages/ItemCSVMapper'));
const PrintSettings = lazy(() => import('./pages/PrintSettings'));
const ChequePrintSettings = lazy(() => import('./pages/ChequePrintSettings'));
const ClassificationSettings = lazy(() => import('./pages/ClassificationSettings'));
const PurchaseReturn = lazy(() => import('./pages/PurchaseReturn'));
const PurchaseReturnHistory = lazy(() => import('./pages/PurchaseReturnHistory'));
const PurchaseOrder = lazy(() => import('./pages/PurchaseOrder'));
const TopSellersAnalysis = lazy(() => import('./pages/TopSellersAnalysis'));
const PurchaseOrderHistory = lazy(() => import('./pages/PurchaseOrderHistory'));
// AutoPurchaseSettings is imported directly at the top (bypass lazy loading)
const SupplierPayment = lazy(() => import('./pages/SupplierPaymentNew'));
const RackMaster = lazy(() => import('./pages/RackMaster'));
const ItemLocation = lazy(() => import('./pages/ItemLocation'));
const StoreMigration = lazy(() => import('./pages/StoreMigration'));
const QRCodeLabelSettings = lazy(() => import('./pages/QRCodeLabelSettings'));
const ProductEnrichment = lazy(() => import('./pages/ProductEnrichment'));
// Stock Audit - Only using Enhanced (Multi) version
const StockAuditEnhanced = lazy(() => import('./pages/StockAuditEnhanced'));
const MobileStockAuditNew = lazy(() => import('./pages/MobileStockAuditNew'));
const AuditCycle = lazy(() => import('./pages/AuditCycle'));
const AuditCycleLogin = lazy(() => import('./pages/AuditCycleLogin'));
const ConsumerMaster = lazy(() => import('./pages/ConsumerMaster'));
const CarePointsAdmin = lazy(() => import('./pages/CarePointsAdmin'));
const CustomerMaster = lazy(() => import('./pages/CustomerMaster'));
const StoreMaster = lazy(() => import('./pages/StoreMaster'));
const Settings = lazy(() => import('./pages/Settings'));
const OldHistoryMigration = lazy(() => import('./pages/OldHistoryMigration'));
const StoreCodeMigration = lazy(() => import('./pages/StoreCodeMigration'));
const LocationSync = lazy(() => import('./pages/LocationSync'));
const UserRoleManagement = lazy(() => import('./pages/UserRoleManagement'));
const SimpleTemplatePicker = lazy(() => import('./pages/SimpleTemplatePicker'));
const StockReceive = lazy(() => import('./pages/StockReceive'));
const StockTransferEntry = lazy(() => import('./pages/StockTransferEntry'));
const StockTransferHistory = lazy(() => import('./pages/StockTransferHistory'));
const CustomerDisplay = lazy(() => import('./pages/CustomerDisplay'));
const PaymentPage = lazy(() => import('./pages/PaymentPage'));
const FeedbackAnalytics = lazy(() => import('./pages/FeedbackAnalytics'));
const JitStockReport = lazy(() => import('./pages/JitStockReport'));
// Smart JIT Allocation Module
const JITSmartAllocation = lazy(() => import('./pages/JITSmartAllocation'));
const HoldRegister = lazy(() => import('./pages/HoldRegister'));

// Hiring / Careers Module
const HiringDashboard = lazy(() => import('./pages/HiringDashboard'));
const JobForm = lazy(() => import('./pages/JobForm'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const HiringReports = lazy(() => import('./pages/HiringReports'));
const CareersPage = lazy(() => import('./pages/CareersPage').then(module => ({ default: module.CareersPage })));
const JobDetailPage = lazy(() => import('./pages/CareersPage').then(module => ({ default: module.JobDetailPage })));
const ApplicationSuccessPage = lazy(() => import('./pages/CareersPage').then(module => ({ default: module.ApplicationSuccessPage })));
const CandidateDetail = lazy(() => import('./pages/CandidateDetail'));

// Franchise CRM Module - Care Pharmacy Franchise Sales Intelligence System
const CRMApp = lazy(() => import('./pages/FranchiseCRM/CRMApp'));

// Consumer CRM Module - Retail B2C Customer Relationship Management
const ConsumerCRM = lazy(() => import('./pages/ConsumerCRM'));

// Online Pharmacy Admin Module
const OnlinePharmacyAdmin = lazy(() => import('./pages/OnlinePharmacyAdmin'));

// WhatsApp Business API Module
const WhatsAppConfig = lazy(() => import('./pages/WhatsApp/WhatsAppConfig'));
const WhatsAppTemplates = lazy(() => import('./pages/WhatsApp/WhatsAppTemplates'));
const WhatsAppMessaging = lazy(() => import('./pages/WhatsApp/WhatsAppMessaging'));
const WhatsAppLiveChat = lazy(() => import('./pages/WhatsApp/WhatsAppLiveChat'));
const WhatsAppAnalytics = lazy(() => import('./pages/WhatsApp/WhatsAppAnalytics'));
const WhatsAppBilling = lazy(() => import('./pages/WhatsApp/WhatsAppBilling'));
const WhatsAppSalesInvoiceConfig = lazy(() => import('./pages/WhatsApp/WhatsAppSalesInvoiceConfig'));
const WhatsAppTemplateLogs = lazy(() => import('./pages/WhatsApp/WhatsAppTemplateLogs'));

// Mobile App Preview
const MobileAppPreview = lazy(() => import('./pages/MobileAppPreview'));

// Public marketing landing page (/welcome) — Phase 5.3
const Welcome = lazy(() => import('./pages/Welcome'));

// RxGenius AI Extended
const AICommandCenter = lazy(() => import('./pages/AICommandCenter'));

// UPI Transactions
const UPITransactions = lazy(() => import('./pages/UPITransactions'));

const Footer = lazy(() => import('./components/Footer'));
const ItemInfoModal = lazy(() => import('./components/ItemInfoModal'));
const RxGeniusAI = lazy(() => import('./components/RxGeniusAI'));
const RxGeniusNotifications = lazy(() => import('./components/RxGeniusNotifications'));

function AppContent({ user, handleLogin, handleLogout }) {
  const navigate = useNavigate();
  const [isMobileRoute, setIsMobileRoute] = useState(false);
  const [showGlobalItemInfo, setShowGlobalItemInfo] = useState(false);
  
  // Initialize global keyboard shortcuts
  useGlobalKeyboardShortcuts();

  useEffect(() => {
    // Check if current route is mobile, customer-display, lucky-draw
    // Hide footer for these routes to prevent button overlap
    const path = window.location.pathname;
    setIsMobileRoute(
      path.startsWith('/mobile') || 
      path.startsWith('/welcome') ||  // Public marketing landing page — no footer/nav chrome
      path.startsWith('/customer-display') || 
      path.startsWith('/lucky-draw/screen') || 
      path.startsWith('/lucky-draw/live') || 
      path.startsWith('/lucky-draw/view') ||
      path.startsWith('/vendor') ||  // Vendor Portal PWA - hide footer
      path.startsWith('/davaai') ||  // Davaai QuickCommerce app - hide footer/chrome
      path.startsWith('/franchise-crm')  // Franchise CRM PWA - hide footer
    );
  }, [window.location.pathname]);

  // Global Item Information shortcut (Ctrl+Shift+I)
  useEffect(() => {
    const handleItemInfoShortcut = (event) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'I') {
        event.preventDefault();
        if (user) {
          setShowGlobalItemInfo(true);
        }
      }
      // Close on Escape
      if (event.key === 'Escape' && showGlobalItemInfo) {
        setShowGlobalItemInfo(false);
      }
    };
    
    window.addEventListener('keydown', handleItemInfoShortcut);
    return () => window.removeEventListener('keydown', handleItemInfoShortcut);
  }, [user, showGlobalItemInfo]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      // CTRL+I for Item Master
      if (event.ctrlKey && event.key === 'i') {
        event.preventDefault();
        if (user) {
          navigate('/items');
        }
      }
      // CTRL+B for Batch Master
      if (event.ctrlKey && event.key === 'b') {
        event.preventDefault();
        if (user) {
          navigate('/batches');
        }
      }
      // CTRL+S for Supplier Master
      if (event.ctrlKey && event.key === 's') {
        event.preventDefault();
        if (user) {
          navigate('/suppliers');
        }
      }
      // CTRL+P for Purchase Entry
      if (event.ctrlKey && event.key === 'p') {
        event.preventDefault();
        if (user) {
          navigate('/purchase');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, navigate]);

  return (
    <>
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
        {/* Print Preview - Public route (no auth needed) for auto-print after dispatch */}
        <Route 
          path="/print-preview" 
          element={<PrintPreviewPage />} 
        />
        <Route 
          path="/login" 
          element={!user ? <Login onLogin={handleLogin} /> : <Navigate to="/" />} 
        />
      <Route 
        path="/" 
        element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* Mobile Routes */}
      <Route 
        path="/mobile" 
        element={user ? <MobileDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobile/sales-b2c" 
        element={user ? <MobileSalesB2C user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobile/sales-b2b" 
        element={user ? <MobileSalesB2B user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobile/purchase" 
        element={user ? <MobilePurchase user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobile/stock-audit" 
        element={user ? (
          <StaffRestrictedRoute user={user}>
            <MobileStockAudit user={user} onLogout={handleLogout} />
          </StaffRestrictedRoute>
        ) : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobile/wms" 
        element={user ? <MobileWMS user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      <Route 
        path="/track-orders" 
        element={user ? <TrackOrders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/stock-receive" 
        element={user ? <StockReceive user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/stock-transfer" 
        element={user ? <StockTransferEntry user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/stock-transfer-history" 
        element={user ? <StockTransferHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/order-fulfillment" 
        element={user ? <OrderFulfillmentDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/pharmarack-auto-order" 
        element={user ? <PharmarackAutoOrder user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/perf-console" 
        element={user ? <PerfConsole user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/items" 
        element={user ? <ItemMasterNew user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/categories" 
        element={user ? <CategoryMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/import" 
        element={user ? <ImportItems user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route
        path="/database-backup"
        element={user ? <DatabaseBackup user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
      />

      <Route 
        path="/batches" 
        element={user ? <BatchMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/suppliers" 
        element={user ? <SupplierMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase" 
        element={user ? <PurchaseEntry user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-history" 
        element={user ? <PurchaseHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-history-b2b" 
        element={user ? <PurchaseHistoryB2B user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/gmail-import" 
        element={user ? <GmailImport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/supplier-csv-mapper" 
        element={user ? <SupplierCSVMapper user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/item-csv-mapper" 
        element={user ? <ItemCSVMapper user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-return" 
        element={user ? <PurchaseReturn user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-return-history" 
        element={user ? <PurchaseReturnHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-order" 
        element={user ? <PurchaseOrder user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/top-sellers" 
        element={user ? <TopSellersAnalysis user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-order-history" 
        element={user ? <PurchaseOrderHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/auto-purchase-settings" 
        element={user ? <AutoPurchaseSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/supplier-payment" 
        element={user ? <SupplierPayment user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/customer-receipt" 
        element={user ? <CustomerReceipt user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/store-track" 
        element={user ? <StoreTrack user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-return-request" 
        element={user ? <PurchaseReturnRequest user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/my-return-requests" 
        element={user ? <MyReturnRequests user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/return-approval" 
        element={user ? <ReturnApprovalDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/tray-management" 
        element={user ? <TrayManagementDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase-return-view" 
        element={user ? <PurchaseReturnView user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/return-policy" 
        element={user ? <ReturnPolicySettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />

      {/* NEW Return Module Routes (2025-11-25) */}
      <Route 
        path="/store-return-to-warehouse" 
        element={user ? <StoreReturnToWarehouse user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/store-return-history" 
        element={user ? <StoreReturnHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/consumer-return" 
        element={user ? <ConsumerReturn user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/warehouse-tray-management" 
        element={user ? <WarehouseTrayManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/supplier-return-generation" 
        element={user ? <SupplierReturnGeneration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/manual-supplier-return" 
        element={user ? <ManualSupplierReturn user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/warehouse-sales-return" 
        element={user ? <CreditNote user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/credit-notes" 
        element={user ? <CreditNote user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />

      <Route 
        path="/print-template-builder" 
        element={user ? <PrintTemplateBuilder user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/paytm-payments" 
        element={user ? <PaytmPaymentDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/keyboard-shortcuts" 
        element={user ? <KeyboardShortcutsHelp user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/data-migration" 
        element={user ? <DataMigration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/stock-migration" 
        element={user ? <StockMigrationDryRun user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/paytm-pos" 
        element={user ? <PaytmPOSManager user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/einvoice" 
        element={user ? <EInvoiceManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/gstr1" 
        element={user ? <GSTR1Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/gstr3b" 
        element={user ? <GSTR3BDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/supplier-payment-debug" 
        element={<SupplierPaymentDebug />} 
      />
      <Route 
        path="/loyalty" 
        element={user ? <LoyaltyDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/shift-management" 
        element={user ? <ShiftManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/shift-collection" 
        element={user ? <ShiftCollection user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />

      {/* Lucky Draw */}
      <Route path="/lucky-draw" element={user ? <LuckyDraw user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/offers" element={user ? <OffersPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/lucky-draw/screen1" element={<LuckyDrawScreen1 />} />
      <Route path="/lucky-draw/screen2" element={<LuckyDrawScreen2 />} />
      <Route path="/lucky-draw/live" element={<LuckyDrawLive />} />
      <Route path="/lucky-draw/view" element={<LuckyDrawView />} />

      {/* Report Routes */}
      <Route 
        path="/reports/overall" 
        element={user ? <OverallPerformance user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/sales-register" 
        element={user ? <SalesRegister user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/margin-performance" 
        element={user ? <MarginPerformanceReport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/consumer-merge-performance" 
        element={user ? <ConsumerMergePerformance user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/purchase-register" 
        element={user ? <PurchaseRegister user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/expenses" 
        element={user ? <ExpenseModule user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/reports/pending-invoices" 
        element={user ? <PendingInvoicesReport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />

      <Route 
        path="/inventory" 
        element={user ? <Inventory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/customers" 
        element={user ? <CustomerMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/rack-master" 
        element={user ? <RackMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/item-location" 
        element={user ? <ItemLocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/store-migration" 
        element={user ? <StoreMigration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/stock-audit" 
        element={<Navigate to="/stock-audit-multi" replace />} 
      />
      <Route 
        path="/stock-audit-multi" 
        element={user ? (
          <StaffRestrictedRoute user={user}>
            <StockAuditEnhanced user={user} onLogout={handleLogout} />
          </StaffRestrictedRoute>
        ) : <Navigate to="/login" />} 
      />
      <Route 
        path="/mobilestock" 
        element={user ? (
          <StaffRestrictedRoute user={user}>
            <MobileStockAuditNew user={user} onLogout={handleLogout} />
          </StaffRestrictedRoute>
        ) : <Navigate to="/login" />} 
      />
      <Route 
        path="/jit-stock-report" 
        element={user ? <JitStockReport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      {/* Audit Cycle Module - Separate Login */}
      <Route 
        path="/audit-cycle-login" 
        element={<AuditCycleLogin onLogin={(auditUser) => {
          // Store audit user in a separate state if needed
          localStorage.setItem('auditCycleUser', JSON.stringify(auditUser));
        }} />} 
      />
      <Route 
        path="/audit-cycle" 
        element={
          // Check for either main ERP user OR audit-specific session
          user ? (
            <AuditCycle user={user} onLogout={handleLogout} />
          ) : localStorage.getItem('auditCycleUser') ? (
            <AuditCycle 
              user={JSON.parse(localStorage.getItem('auditCycleUser'))} 
              onLogout={() => {
                localStorage.removeItem('auditCycleUser');
                localStorage.removeItem('auditCycleToken');
                window.location.href = '/audit-cycle-login';
              }} 
            />
          ) : (
            <Navigate to="/audit-cycle-login" />
          )
        } 
      />
      {/* Smart JIT Allocation Module */}
      <Route 
        path="/jit" 
        element={user ? <JITSmartAllocation user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hold-register" 
        element={user ? <HoldRegister user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-b2c" 
        element={user ? <SalesInvoiceB2C user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-history-b2c" 
        element={user ? <SalesHistoryB2C user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-b2b" 
        element={user ? <SalesInvoiceB2B user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-history-b2b" 
        element={user ? <SalesHistoryB2B user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-order-entry" 
        element={user ? <SalesOrderEntry user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/sales-order-history" 
        element={user ? <SalesOrderHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/online-orders" 
        element={user ? <OnlineOrders user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />

      <Route 
        path="/consumer-master" 
        element={user ? <ConsumerMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/care-points" 
        element={user ? <CarePointsAdmin user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/store-master" 
        element={user ? <StoreMaster user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/settings" 
        element={user ? <Settings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/user-role-management" 
        element={user ? <UserRoleManagement user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/simple-template-picker" 
        element={user ? <SimpleTemplatePicker user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/purchase/view/:id" 
        element={user ? <PurchaseHistory user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/print-settings" 
        element={user ? <PrintSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/cheque-print-settings" 
        element={user ? <ChequePrintSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/classification-settings" 
        element={user ? <ClassificationSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/admin/old-history-migration" 
        element={user ? <OldHistoryMigration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/admin/store-migration" 
        element={user ? <StoreCodeMigration user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/admin/location-sync" 
        element={user ? <LocationSync user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/qrcode-settings" 
        element={user ? <QRCodeLabelSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/product-enrichment" 
        element={user ? <ProductEnrichment user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/customer-display" 
        element={<CustomerDisplay />}
      />
      {/* Public Payment Page - No auth required for customer payments */}
      <Route 
        path="/pay/:linkId" 
        element={<PaymentPage />}
      />
      <Route 
        path="/feedback-analytics" 
        element={user ? <FeedbackAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />}
      />
      <Route 
        path="/franchise-discount-import" 
        element={user ? <FranchiseDiscountImport user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/batch-import-old-code" 
        element={user ? <BatchImportOldCode user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* HR Module Routes */}
      <Route 
        path="/hr" 
        element={user ? <HRDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/attendance" 
        element={user ? <HRAttendance user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/tasks" 
        element={user ? <HRTasks user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/leaves" 
        element={user ? <HRLeaves user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/approvals" 
        element={user ? <HRApprovals user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/reports" 
        element={user ? <HRReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/payouts" 
        element={user ? <HRPayouts user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      <Route 
        path="/hr/settings" 
        element={user ? <HRSettings user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* Notification Center */}
      <Route 
        path="/notification-center" 
        element={user ? <NotificationCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* Dispatch Register Routes */}
      <Route 
        path="/dispatch-register" 
        element={user ? <DispatchRegister user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* GBP Command Center */}
      <Route 
        path="/gbp-command-center" 
        element={user ? <GBPCommandCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} 
      />
      
      {/* Packer Interface - Separate Mobile URL (no sidebar) */}
      <Route 
        path="/packer" 
        element={<PackerInterface />} 
      />
      
      {/* ========== VENDOR PORTAL ROUTES (Supplier PWA) ========== */}
      {/* These routes are public for suppliers - no main ERP auth required */}
      <Route path="/vendor/login" element={<SupplierLogin />} />
      <Route path="/vendor/dashboard" element={<SupplierDashboard />} />
      <Route path="/vendor/requirements" element={<PurchaseRequirements />} />
      <Route path="/vendor/orders" element={<VendorConfirmedOrders />} />
      <Route path="/vendor/profile" element={<SupplierProfile />} />
      <Route path="/vendor/portfolio" element={<BusinessPortfolio />} />
      <Route path="/vendor/outstanding" element={<OutstandingAccount />} />
      <Route path="/vendor/notifications" element={<VendorNotifications />} />
      <Route path="/vendor/own-platform" element={<VendorOwnPlatform />} />
      {/* Redirect /vendor to /vendor/login */}
      <Route path="/vendor" element={<Navigate to="/vendor/login" />} />
      
      {/* ========== VENDOR ADMIN ROUTES (ERP Admin) ========== */}
      {/* These routes are for ERP admins to manage requirements and bids */}
      <Route path="/vendor-admin/requirements" element={<VendorRequirements user={user} onLogout={handleLogout} />} />
      <Route path="/vendor-admin/requirements/create" element={<CreateRequirement user={user} onLogout={handleLogout} />} />
      <Route path="/vendor-admin/requirements/:id" element={<RequirementDetails user={user} onLogout={handleLogout} />} />
      <Route path="/vendor-admin/performance" element={<VendorPerformance user={user} onLogout={handleLogout} />} />
      <Route path="/vendor-admin/own-platform" element={<AdminOwnPlatform user={user} onLogout={handleLogout} />} />
      
      {/* ========== FRANCHISE CRM ROUTES ========== */}
      {/* Standalone PWA for Care Pharmacy Franchise Sales - manages its own auth */}
      <Route path="/franchise-crm/*" element={<CRMApp />} />

      {/* ========== CONSUMER CRM ROUTES ========== */}
      {/* Consumer CRM - Retail B2C Customer Relationship Management */}
      <Route path="/consumer-crm" element={user ? <ConsumerCRM user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />

      {/* ========== RXGENIUS AI COMMAND CENTER ========== */}
      {/* AI-powered features: Prescription Scanner, Pharma News, Inventory Intelligence */}
      <Route path="/ai-command-center" element={user ? <AICommandCenter user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />

      {/* ========== UPI TRANSACTIONS ========== */}
      {/* View ICICI UPI payment transactions */}
      <Route path="/upi-transactions" element={user ? <UPITransactions user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />

      {/* ========== ONLINE PHARMACY ADMIN ========== */}
      {/* Admin interface for managing Care Pharmacy Mobile App - Warehouse/Admin only */}
      <Route path="/online-pharmacy-admin" element={user ? <OnlinePharmacyAdmin user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/davaai-portal-manager" element={user ? <DavaaiPortalManager user={user} /> : <Navigate to="/login" />} />

      {/* ========== YEAR END CLOSING ========== */}
      {/* Year End Closing Tool - Warehouse/Admin only */}
      <Route path="/year-end-closing" element={user ? <YearEndClosing user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />

      {/* ========== WHATSAPP BUSINESS API ROUTES ========== */}
      <Route path="/whatsapp/config" element={user ? <WhatsAppConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/templates" element={user ? <WhatsAppTemplates user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/messaging" element={user ? <WhatsAppMessaging user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/chat" element={user ? <WhatsAppLiveChat user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/analytics" element={user ? <WhatsAppAnalytics user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/billing" element={user ? <WhatsAppBilling user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/sales-invoice-config" element={user ? <WhatsAppSalesInvoiceConfig user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/whatsapp/template-logs" element={user ? <WhatsAppTemplateLogs user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      
      {/* ========== HIRING / CAREERS MODULE ========== */}
      {/* Admin Routes (require login) */}
      <Route path="/hiring" element={user ? <HiringDashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/hiring/jobs/new" element={user ? <JobForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/hiring/jobs/:jobId" element={user ? <JobForm user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/hiring/jobs/:jobId/view" element={user ? <JobDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/hiring/candidates/:candidateId" element={user ? <CandidateDetail user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/hiring/reports" element={user ? <HiringReports user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      
      {/* Public Careers Routes (no auth required) */}
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/job/:jobSlug" element={<JobDetailPage />} />
      <Route path="/careers/application-success" element={<ApplicationSuccessPage />} />
      
      {/* ========== STAFF PROFILE ========== */}
      <Route path="/staff-list" element={user ? <StaffList user={user} /> : <Navigate to="/login" />} />
      <Route path="/my-profile" element={user ? <StaffProfile user={user} /> : <Navigate to="/login" />} />
      <Route path="/staff-profile/:profileId" element={user ? <StaffProfile user={user} /> : <Navigate to="/login" />} />
      <Route path="/staff-profile/:profileId/id-card" element={user ? <StaffIDCard user={user} /> : <Navigate to="/login" />} />
      
      {/* ========== TARGET PERFORMANCE ========== */}
      <Route path="/target-performance" element={user ? <TargetPerformance user={user} /> : <Navigate to="/login" />} />
      
      {/* ========== MOBILE APP PREVIEW ========== */}
      {/* Public route for testing mobile app without ERP auth */}
      <Route path="/mobile-preview" element={<MobileAppPreview />} />

      {/* ========== PUBLIC MARKETING LANDING PAGE ========== */}
      {/* Used in social-media campaigns. Drives app installs + captures leads. */}
      <Route path="/welcome" element={<Welcome />} />

      {/* ========== DAVAAI - Smart Pharma Supply (separate B2B QuickCommerce app) ========== */}
      <Route path="/davaai/*" element={<DavaaiApp />} />
      <Route path="/davaai-admin/*" element={<DavaaiAdmin />} />
      <Route path="/davaai-rider/*" element={<DavaaiRiderApp />} />
    </Routes>
    </Suspense>
    <Suspense fallback={null}>
      {user && !isMobileRoute && <Footer />}
      
      {/* Global Item Information Modal (Ctrl+Shift+I) */}
      {showGlobalItemInfo && (
        <ItemInfoModal 
          itemCode="" 
          itemName="" 
          onClose={() => setShowGlobalItemInfo(false)} 
        />
      )}
      
      {/* RxGenius AI - Floating Smart Assistant Widget */}
      {user && !isMobileRoute && <RxGeniusAI user={user} />}
      
      {/* RxGenius AI - Floating Notifications Banner */}
      {user && !isMobileRoute && <RxGeniusNotifications user={user} enabled={true} />}
    </Suspense>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // CRITICAL: Loading state to prevent redirect before user loads
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  // Revalidate session against backend. Returns true if user is still valid.
  // Fail-OPEN on network/server errors so a brief backend hiccup doesn't
  // log everyone out. Only logs out on explicit 401/403/404.
  const revalidateSession = useCallback(async (cachedUser, source = 'boot') => {
    if (!cachedUser?.id) return false;
    try {
      const resp = await fetch(`${BACKEND_URL}/api/auth/me?user_id=${encodeURIComponent(cachedUser.id)}`);
      if (resp.status === 401 || resp.status === 403 || resp.status === 404) {
        console.warn(`🔒 Session invalidated by backend (${source}, ${resp.status})`);
        // Wipe everything
        localStorage.removeItem('pharma_user');
        localStorage.removeItem('session_token');
        localStorage.removeItem('current_store');
        localStorage.removeItem('erp_session_id');
        localStorage.removeItem('auditCycleUser');
        localStorage.removeItem('auditCycleToken');
        try {
          const { toast } = await import('sonner');
          toast.error('Your session has been ended by an administrator. Please log in again.', { duration: 6000 });
        } catch { /* sonner not loaded yet */ }
        sessionStorage.setItem('logout_reason',
          'Your session has been ended by an administrator. Please log in again.');
        setUser(null);
        // Give the toast a moment then redirect
        setTimeout(() => { window.location.href = '/login'; }, 600);
        return false;
      }
      // Status OK — refresh user details so role / store changes propagate
      if (resp.ok) {
        try {
          const data = await resp.json();
          if (data?.user) {
            const refreshed = { ...cachedUser, ...data.user };
            localStorage.setItem('pharma_user', JSON.stringify(refreshed));
            setUser(refreshed);
          }
        } catch { /* shape mismatch — keep cached */ }
      }
      return true;
    } catch (e) {
      // Network/server error → fail-OPEN (don't log out)
      console.warn(`/auth/me failed (${source}):`, e.message);
      return true;
    }
  }, [BACKEND_URL]);

  useEffect(() => {
    setRequestIdentity(user);
  }, [user]);

  useEffect(() => {
    const savedUser = localStorage.getItem('pharma_user');
    if (savedUser) {
      const parsed = JSON.parse(savedUser);
      setUser(parsed);
      // Validate against backend — if user was deleted while tab was closed,
      // this will wipe state + redirect. Defer to next tick so we don't
      // trigger setState inside the same React commit.
      Promise.resolve().then(() => revalidateSession(parsed, 'boot'));
    } else {
      // Also validate Audit-Cycle session if main ERP isn't logged in
      const auditUser = localStorage.getItem('auditCycleUser');
      if (auditUser) {
        try {
          const parsedAudit = JSON.parse(auditUser);
          Promise.resolve().then(() => revalidateSession(parsedAudit, 'boot-audit'));
        } catch { /* malformed JSON */ }
      }
    }
    setIsLoading(false); // Done loading - now routes can redirect if needed
  }, [revalidateSession]);
  
  // CRITICAL: Listen for storage changes from other tabs
  // This handles the case when user logs out from another tab
  useEffect(() => {
    const handleStorageChange = (event) => {
      // Check if pharma_user was removed (logout from another tab)
      if (event.key === 'pharma_user') {
        if (event.newValue === null) {
          // User logged out from another tab - clear state in this tab too
          console.log('🔒 Detected logout from another tab - clearing session');
          setUser(null);
          // Redirect to login page
          window.location.href = '/login';
        } else if (event.newValue) {
          // User logged in from another tab - update state
          try {
            const newUser = JSON.parse(event.newValue);
            console.log('🔓 Detected login from another tab - updating session');
            setUser(newUser);
          } catch (e) {
            console.error('Error parsing user data from storage event:', e);
          }
        }
      }
      
      // Also check for session_token changes (for backward compatibility)
      if (event.key === 'session_token' && event.newValue === null) {
        console.log('🔒 Detected token removal from another tab - clearing session');
        setUser(null);
        window.location.href = '/login';
      }
    };
    
    // Add listener for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);
  
  // CRITICAL: Validate session on window focus (when user switches back to this tab)
  useEffect(() => {
    const handleFocus = () => {
      // Re-check localStorage when tab becomes active
      const savedUser = localStorage.getItem('pharma_user');
      const currentStore = localStorage.getItem('current_store');
      
      if (!savedUser && user) {
        // User was logged out from another tab while this tab was inactive
        console.log('🔒 Session invalid on focus - redirecting to login');
        setUser(null);
        window.location.href = '/login';
      } else if (savedUser && user) {
        // ⚠ Validate against backend — catches admin-deleted users (Jun-2026 fix)
        try { revalidateSession(JSON.parse(savedUser), 'focus'); } catch { /* */ }
      } else if (!currentStore && user) {
        // Store was cleared but user still set - potential security issue
        console.log('⚠️ Store cleared but user exists - potential session corruption');
        // Re-validate by checking if user matches localStorage
        if (savedUser) {
          try {
            const storedUser = JSON.parse(savedUser);
            if (storedUser.username !== user.username) {
              console.log('🔒 User mismatch detected - clearing session');
              setUser(null);
              localStorage.removeItem('pharma_user');
              window.location.href = '/login';
            }
          } catch (e) {
            // Invalid JSON - clear everything
            setUser(null);
            localStorage.removeItem('pharma_user');
            window.location.href = '/login';
          }
        }
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user, revalidateSession]);

  // ── Live session heartbeat: keeps the Performance Console's session list fresh and
  //    enforces an admin "force logout" on every device within ~20 seconds.
  useEffect(() => {
    if (!user?.id) return;
    let stopped = false;
    const forceOut = async (reason) => {
      localStorage.removeItem('pharma_user');
      localStorage.removeItem('session_token');
      localStorage.removeItem('current_store');
      localStorage.removeItem('erp_session_id');
      sessionStorage.setItem('logout_reason', reason === 'FORCED_LOGOUT'
        ? 'You have been logged out by the administrator.'
        : 'Your session has ended. Please log in again.');
      setUser(null);
      window.location.href = '/login';
    };
    const beat = async () => {
      if (stopped || document.hidden) return;
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/session/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: localStorage.getItem('erp_session_id') || '',
            user_id: user.id,
            path: window.location.pathname,
          }),
        });
        const d = await r.json();
        if (d && d.active === false) { stopped = true; forceOut(d.reason); }
        else if (d && d.session_id) localStorage.setItem('erp_session_id', d.session_id);
      } catch { /* offline — never log the user out on a network blip */ }
    };
    beat();
    const iv = setInterval(beat, 20000);
    const onVisible = () => { if (!document.hidden) beat(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { stopped = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVisible); };
  }, [user?.id, BACKEND_URL]);

  const handleLogin = async (userData) => {
    setUser(userData);
    localStorage.setItem('pharma_user', JSON.stringify(userData));
    
    // CRITICAL: Set user's default store on login
    // Clear any previous store and set to user's assigned default store
    const defaultStoreCode = userData.storeCode || userData.c_default_store || '501';
    
    try {
      // Fetch the default store details from backend
      const response = await fetch(`${BACKEND_URL}/api/stores`);
      const data = await response.json();
      
      if (data.stores) {
        // Filter stores to only show user's accessible stores
        const accessibleStores = data.stores.filter(s => 
          (userData.storeCodes || []).includes(s.c_store_code) ||
          (userData.accessibleStores || []).includes(s.c_store_code) ||
          s.c_store_code === defaultStoreCode
        );
        
        const defaultStore = accessibleStores.find(s => s.c_store_code === defaultStoreCode) || accessibleStores[0];
        
        if (defaultStore) {
          // Set the user's default store as current store
          localStorage.setItem('current_store', JSON.stringify(defaultStore));
          console.log('✅ Default store set to:', defaultStore.c_store_name, '(' + defaultStoreCode + ')');
          
          // Dispatch custom event to notify components
          window.dispatchEvent(new CustomEvent('store-changed'));
        }
      }
    } catch (error) {
      console.error('Error setting default store:', error);
      // Fallback: Create a minimal store object
      localStorage.setItem('current_store', JSON.stringify({
        c_store_code: defaultStoreCode,
        c_store_name: 'Store ' + defaultStoreCode,
        c_store_type: defaultStoreCode === '501' || defaultStoreCode === '502' ? 'WAREHOUSE' : 'BRANCH'
      }));
      window.dispatchEvent(new CustomEvent('store-changed'));
    }
  };

  const handleLogout = () => {
    const sid = localStorage.getItem('erp_session_id') || '';
    if (sid) {
      fetch(`${BACKEND_URL}/api/auth/session/end`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sid }),
      }).catch(() => {});
      localStorage.removeItem('erp_session_id');
    }
    setUser(null);
    localStorage.removeItem('pharma_user');
    // IMPORTANT: Clear current store on logout
    localStorage.removeItem('current_store');
  };

  // Show loading screen while checking auth state to prevent flash redirect
  if (isLoading) {
    return (
      <div className="App flex items-center justify-center min-h-screen bg-slate-100">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <AppContent user={user} handleLogin={handleLogin} handleLogout={handleLogout} />
      </BrowserRouter>
      {/* Music player hidden across the ERP per request */}
      {false && user && <SpotifyMusicBanner user={user} />}
      <Toaster />
      <RadixToaster />
    </div>
  );
}

export default App;